import { useState } from 'react';
import {
  AccessEndPicker,
  accessEndIso,
  accessEndReady,
  accessRoleLabel,
  Button,
  defaultAccessEnd,
  HelpTip,
  Input,
  TeamRolePicker,
  useSingleSubmit,
} from '@dataroom/shared';
import type {
  AccessEndValue,
  AccessRole,
  InvitePartyRequest,
  RoomAdminRole,
  TeamRole,
} from '@dataroom/shared';
import { parseOwnershipPct } from '@/lib/ownership';
import { Modal } from './Modal';
import { OwnershipPctField } from './OwnershipPctField';

interface InvitePartyDialogProps {
  /** The room's admin role — labels read from the buyer's side in a buyer-run room. */
  adminRole?: RoomAdminRole | null;
  /** How many sellers the room has now (null while unknown): the first seller's
   * ownership defaults to 100%, any other must be chosen. */
  sellerCount: number | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: InvitePartyRequest) => void;
  /** Add this person as one of the room's sellers, with what they own. */
  onAddSeller: (body: { email: string; ownershipPct: number }) => void;
}

/** "Seller" (shown as Owner) adds an owner with an ownership share; every other choice is an access role. */
type InviteChoice = 'seller' | AccessRole;

/** The choices in the order offered — the seller first. */
const INVITE_CHOICES: readonly InviteChoice[] = [
  'seller',
  'seller_team',
  'intermediary_team',
  'diligence_team',
  'custom',
];

const CHOICE_LABELS: Record<InviteChoice, string> = {
  seller: 'Owner — a business owner of the company',
  seller_team: "Seller's team — works in the seller portal with the seller",
  intermediary_team: "Intermediary's team (your side)",
  diligence_team: "Buyer's diligence team (counterparty)",
  custom: 'Custom access',
};

function choiceLabel(choice: InviteChoice, adminRole: RoomAdminRole | null | undefined): string {
  // In a room the buyer runs, the diligence team is the buyer's own (ADR-0039).
  if (adminRole === 'buyer' && choice === 'diligence_team') {
    return accessRoleLabel(choice, adminRole);
  }
  return CHOICE_LABELS[choice];
}

/** The choices whose person can carry team roles (Legal, Financial, ...). */
const ROLE_CHOICES: readonly InviteChoice[] = ['seller_team', 'diligence_team'];

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/**
 * Invite a NEW person into the room by email. The server find-or-creates the
 * actor (they sign in later with an emailed code) and applies the chosen role's default
 * access. "Seller" instead ADDS them as one of the room's sellers, with the share
 * of the company they own (the same add-seller call as the Seller card); nobody is
 * replaced. A seller's-team or buyer's-diligence-team person can also carry team
 * roles (Legal, Financial, ...). Shaping the access of someone ALREADY in the room
 * is a separate control ("Manage access rights"), so this dialog is purely the
 * new-person front door. Presentational: the page owns the mutation and
 * pending/error state.
 */
export function InvitePartyDialog({
  adminRole,
  sellerCount,
  pending,
  error,
  onClose,
  onSubmit,
  onAddSeller,
}: InvitePartyDialogProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteChoice>('diligence_team');
  // null = untouched: 100 for the room's first seller, blank otherwise.
  const [pctText, setPctText] = useState<string | null>(null);
  const [teamRoles, setTeamRoles] = useState<TeamRole[]>([]);
  // A buyer's diligence team never has ongoing access (owner rule 2026-10-21):
  // inviting one asks how long it lasts, two weeks unless changed. In a room the
  // BUYER runs, the diligence team is the buyer's own advisers — no end date.
  const [end, setEnd] = useState<AccessEndValue>(defaultAccessEnd);
  const needsEnd = role === 'diligence_team' && adminRole !== 'buyer';

  const emailTrimmed = email.trim();
  const emailLooksValid = /.+@.+\..+/.test(emailTrimmed);
  const pctValue = pctText ?? (sellerCount === 0 ? '100' : '');
  const parsedPct = parseOwnershipPct(pctValue);
  const pctOk = role !== 'seller' || parsedPct.pct !== null;
  const canSubmit = !pending && emailLooksValid && pctOk && (!needsEnd || accessEndReady(end));

  // A quick double-click on Invite sent two invitations and created two grants
  // (full-deal test 2026-10-08).
  const beginSubmit = useSingleSubmit();
  const submit = () => {
    if (!canSubmit || !beginSubmit(`${emailTrimmed}|${role}|${pctValue}`)) return;
    if (role === 'seller') {
      if (parsedPct.pct !== null) onAddSeller({ email: emailTrimmed, ownershipPct: parsedPct.pct });
    } else {
      const expiresAt = needsEnd ? accessEndIso(end) : null;
      if (needsEnd && !expiresAt) return;
      onSubmit({
        email: emailTrimmed,
        role,
        ...(ROLE_CHOICES.includes(role) && teamRoles.length > 0 ? { teamRoles } : {}),
        ...(expiresAt ? { expiresAt } : {}),
      });
    }
  };

  return (
    <Modal
      title="Invite to room"
      description="Bring a new person into this room by email. They get the role's default access; fine-tune it later with Manage access rights."
      onClose={onClose}
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <span className="text-xs text-muted-foreground">Default access applied on invite.</span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" disabled={!canSubmit} onClick={submit}>
              {pending ? 'Inviting…' : 'Invite'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="invite-email" className="text-sm font-medium">
            Email address
          </label>
          <Input
            id="invite-email"
            type="email"
            placeholder="person@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
          />
          <p className="text-xs text-muted-foreground">
            We create their account and they sign in with a code we email them.
          </p>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="invite-role" className="text-sm font-medium">
              Role in this deal
            </label>
            <HelpTip id="admin.access.invite-role" className="ml-1" />
          </div>
          <select
            id="invite-role"
            className={selectClass}
            value={role}
            onChange={(e) => setRole(e.target.value as InviteChoice)}
            disabled={pending}
          >
            {INVITE_CHOICES.map((r) => (
              <option key={r} value={r}>
                {choiceLabel(r, adminRole)}
              </option>
            ))}
          </select>
          {role === 'seller' ? (
            <p className="text-xs text-muted-foreground" data-testid="invite-seller-help">
              They become one of this room’s owners. Owners together decide LOIs by an
              ownership-weighted majority of the company; the percentages cannot add up to more than
              100%.
            </p>
          ) : role === 'seller_team' ? (
            <p className="text-xs text-muted-foreground">
              They work in the seller portal alongside the owners, with the same access to the
              owners’ documents and checklist. They can read letters of intent only if tagged Legal;
              only the owners decide them.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Sets their starting access — a counterparty starts teaser-only; your own-side team
              gets broad access.
            </p>
          )}
        </div>

        {role === 'seller' ? (
          <OwnershipPctField value={pctValue} onChange={setPctText} disabled={pending} />
        ) : null}
        {ROLE_CHOICES.includes(role) ? (
          <TeamRolePicker
            value={teamRoles}
            onChange={setTeamRoles}
            disabled={pending}
            legalNote={role === 'seller_team'}
            legendAside={<HelpTip id="admin.access.team-roles" />}
          />
        ) : null}
        {needsEnd ? (
          <AccessEndPicker
            value={end}
            onChange={setEnd}
            disabled={pending}
            legend="How long does their access last?"
            legendAside={<HelpTip id="admin.access.end-picker" />}
          />
        ) : null}
      </div>
    </Modal>
  );
}
